import * as React from 'react';
import {
  handleBlurFormattedTextField,
  handleChangeFormattedTextField,
  handleFormattedTextFieldEffect,
} from '@tock/shared/ui/forms/formHelpers';
import { usePrevious } from '@tock/hooks';
import TextField, { TextFieldProps } from '../TextField';
import _ from 'lodash';

export interface FormattedTextFieldProps extends TextFieldProps {
  filter: InputFilter;
}

/**
 * Use `TextField` for rendering a text field or text area with autosizing
 *
 * ```js
 * import { FormattedTextField } from 'ui';
 * ```
 */
const FormattedTextField: React.FunctionComponent<FormattedTextFieldProps> = ({
  filter,
  value,
  onChange,
  onBlur,
  ...rest
}) => {
  const [viewValue, setViewValue] = React.useState<string>(
    value && typeof value === 'string' && value.length > 0
      ? filter
        ? _.toString(filter.formatModelValue(value))
        : ''
      : ''
  );

  const prevValue = usePrevious(value);

  React.useEffect(
    () => handleFormattedTextFieldEffect(value, prevValue, filter, setViewValue),
    [filter, prevValue, value]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    handleChangeFormattedTextField(event, filter, setViewValue, onChange);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) =>
    handleBlurFormattedTextField(event, filter, setViewValue, onBlur);

  return (
    <TextField
      value={filter ? viewValue : value}
      {...rest}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

export default FormattedTextField;

// https://github.com/mui-org/material-ui/issues/13898 see for workaround for start adornment
